import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TwoThumbsDraggableTrack } from '../../DraggableTracks';
import MoneyInput from '../../Input/MoneyInput';
import OdometerInput from '../../Input/OdometerInput';
import YearInput from '../../Input/YearInput';
import Toggle from '../../Toggle';
import {
  Container,
  InputContainer,
  InputOptionContainer,
  InputOptionTitle,
  InputRangeContainer,
  SliderContainer,
  StyledInput,
  UnitsContainer,
} from './RangeFilter.styles';

export enum InputType {
  Money,
  Odometer,
  Year,
}

interface RangeFilterProps {
  minValue: number;
  maxValue: number;
  stepValue: number;
  inputType: InputType;
  toggleOptions?: Array<string>;
  toggleInitialValue?: string;
  handleChange?: (startRange: number, endRange: number, unit?: string) => void;
  values?: number[];
}

const RangeFilter = ({
  minValue,
  maxValue,
  stepValue,
  inputType,
  toggleOptions,
  handleChange,
  toggleInitialValue,
  values,
}: RangeFilterProps) => {
  const [toggleValue, setToggleValue] = useState(toggleInitialValue ?? toggleOptions?.[0]);
  const [startRange, setStartRange] = useState(values[0] || minValue || 0);
  const [endRange, setEndRange] = useState(values[1] || maxValue || 100);
  const [errorStartRange, setErrorStartRange] = useState(false);
  const [errorEndRange, setErrorEndRange] = useState(false);

  const { t } = useTranslation();

  const handleOnEditStartRange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const numberPattern = /\d+/g;
    const value = Number(event?.target?.value?.match(numberPattern)?.join('') || '0');

    if (value < endRange && value >= minValue && value <= maxValue) {
      setStartRange(value);
      handleChange?.(value, endRange, toggleValue);
      if (errorStartRange) setErrorStartRange(false);
    } else {
      setErrorStartRange(true);
    }
  };

  const handleOnEditEndRange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const numberPattern = /\d+/g;
    const value = Number(event?.target?.value?.match(numberPattern)?.join('') || '0');

    if (value > startRange && value <= maxValue && value >= minValue) {
      setEndRange(value);
      handleChange?.(startRange, value, toggleValue);
      if (errorEndRange) setErrorEndRange(false);
    } else {
      setErrorEndRange(true);
    }
  };

  const renderInput = (
    value: number,
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void,
    rangePosition: 'rangeStart' | 'rangeEnd',
  ) => {
    switch (inputType) {
      case InputType.Money:
        return (
          <MoneyInput
            value={value}
            customInput={StyledInput}
            onChange={onChange}
            inputProps={{ error: rangePosition === 'rangeStart' ? errorStartRange : errorEndRange }}
          />
        );
      case InputType.Odometer:
        return (
          <OdometerInput
            value={value}
            unit={toggleValue || ''}
            customInput={StyledInput}
            onChange={onChange}
            inputProps={{ error: rangePosition === 'rangeStart' ? errorStartRange : errorEndRange }}
          />
        );
      case InputType.Year:
        return (
          <YearInput
            value={value}
            customInput={StyledInput}
            onChange={onChange}
            inputProps={{ error: rangePosition === 'rangeStart' ? errorStartRange : errorEndRange }}
          />
        );
      default:
        return <></>;
    }
  };

  return (
    <Container>
      {toggleOptions && (
        <UnitsContainer>
          <Toggle
            value={toggleValue}
            options={toggleOptions}
            onSelectSwitch={(value) => {
              setToggleValue(value);
              handleChange?.(startRange, endRange, value);
            }}
          />
        </UnitsContainer>
      )}
      <InputRangeContainer>
        <InputOptionContainer>
          <InputOptionTitle>{t('filters.from')}</InputOptionTitle>
          <InputContainer>{renderInput(startRange, (ev) => handleOnEditStartRange(ev), 'rangeStart')}</InputContainer>
        </InputOptionContainer>
        <InputOptionContainer>
          <InputOptionTitle>{t('filters.to')}</InputOptionTitle>
          <InputContainer>{renderInput(endRange, (ev) => handleOnEditEndRange(ev), 'rangeEnd')}</InputContainer>
        </InputOptionContainer>
      </InputRangeContainer>
      <SliderContainer>
        <TwoThumbsDraggableTrack
          step={stepValue}
          min={minValue}
          max={maxValue}
          values={[startRange, endRange]}
          onValuesChange={(min, max) => {
            setStartRange(min);
            setEndRange(max);
            handleChange?.(min, max, toggleValue);
            if (errorStartRange || (errorEndRange && min < max)) {
              setErrorEndRange(false);
              setErrorStartRange(false);
            }
          }}
        />
      </SliderContainer>
    </Container>
  );
};

export default RangeFilter;
